import { useMutation, UseMutationResult } from "@tanstack/react-query"
import { AxiosResponse, create } from "axios"
import { axiosPost, axiosPut, handleRequestError } from "~/lib"

// Create an axios instance without credentials, base URL and the auth interceptor
const axiosFilesInstance = create({
  withCredentials: false,
})

axiosFilesInstance.interceptors.response.use(
  (response) => response,
  (error) => handleRequestError(error, { skipAuthErrors: true })
)

export type FilePurpose = "ORG_LOGO" | "ATTACHMENT"

type FileUploadPayload = {
  name: string
  size: number
  file: File
  purpose?: FilePurpose
}

type MutationVariables = {
  payload: FileUploadPayload
  onProgress?: (progress: number) => void
  signal?: AbortSignal
}

type FileUploadRequest = {
  name: string
  size: number
  purpose?: FilePurpose
}

type FileUploadResponse = {
  file_id: GUID
  upload_url: string
  original_name: string
}

export function useUploadFiles(): UseUploadFilesReturn {
  return useMutation({
    mutationFn: async ({ payload, onProgress, signal }: MutationVariables) => {
      try {
        const fileUploadPayload: FileUploadRequest = {
          name: payload.name,
          size: payload.size,
          purpose: payload.purpose,
        }

        const { data } = await axiosPost<FileUploadRequest, AxiosResponse<FileUploadResponse>>({
          url: `/files`,
          payload: fileUploadPayload,
          signal,
        })

        await axiosPut<File, unknown>({
          instance: axiosFilesInstance,
          url: data.upload_url,
          payload: payload.file,
          ContentType: payload.file.type,
          setProgress: onProgress,
          signal,
        })

        return {
          id: data.file_id,
          original_name: data.original_name,
        }
      } catch (error) {
        if (signal?.aborted) {
          throw new Error("Upload cancelled")
        }

        throw error
      }
    },
  })
}

interface FileResponse {
  id: string
  original_name: string
}

export type UseUploadFilesReturn = UseMutationResult<FileResponse, Error, MutationVariables, unknown>
